import { FormControlLabel, Stack, Switch, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { FormDialog } from '../../../shared/components/FormDialog';
import { DatePickerPtBrField } from '../../../shared/components/DatePickerPtBr';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { employeeFaltaInputSchema, type EmployeeFaltaFormValues } from './schemas';
import type { EmployeeFaltaDto, EmployeeFaltaInput } from './types';

export interface EmployeeFaltaFormDialogProps {
  open: boolean;
  falta: EmployeeFaltaDto | null;
  hooks: {
    useCreate: () => UseMutationResult<EmployeeFaltaDto, Error, EmployeeFaltaInput>;
    useUpdate: () => UseMutationResult<
      EmployeeFaltaDto,
      Error,
      { id: string; input: EmployeeFaltaInput }
    >;
  };
  onClose: () => void;
}

const DEFAULT_VALUES: EmployeeFaltaFormValues = {
  dataFalta: new Date().toISOString().slice(0, 10),
  justificada: false,
  motivo: '',
};

export function EmployeeFaltaFormDialog({
  open,
  falta,
  hooks,
  onClose,
}: EmployeeFaltaFormDialogProps): JSX.Element {
  const { t } = useTranslation();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    EmployeeFaltaFormValues,
    EmployeeFaltaDto,
    EmployeeFaltaInput,
    string
  >({
    open,
    entity: falta,
    schema: employeeFaltaInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (f) => ({
      dataFalta: f.dataFalta,
      justificada: f.justificada,
      motivo: f.motivo ?? '',
    }),
    toInput: (v) => ({
      dataFalta: v.dataFalta,
      justificada: v.justificada,
      motivo: v.motivo,
    }),
    getId: (f) => f.dataFalta,
    hooks: { useCreate: hooks.useCreate, useUpdate: hooks.useUpdate },
    messages: {
      created: t('features.colaboradores.faltas.toasts.created'),
      updated: t('features.colaboradores.faltas.toasts.updated'),
    },
    focusField: undefined,
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.colaboradores.faltas.dialog.editTitle')
          : t('features.colaboradores.faltas.dialog.newTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="sm"
    >
      <Stack spacing={2}>
        <DatePickerPtBrField
          control={form.control}
          name="dataFalta"
          label={t('features.colaboradores.faltas.fields.dataFalta')}
          disabled={isEdit}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText: isEdit
                ? t('features.colaboradores.faltas.fields.dataFaltaLockedHelper')
                : t('features.colaboradores.faltas.fields.dataFaltaHelper'),
            },
          }}
        />

        <Controller
          control={form.control}
          name="justificada"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label={t('features.colaboradores.faltas.fields.justificada')}
            />
          )}
        />

        <TextField
          label={t('features.colaboradores.faltas.fields.motivo')}
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          inputProps={{ maxLength: 500 }}
          error={Boolean(form.formState.errors.motivo)}
          helperText={
            form.formState.errors.motivo?.message ??
            t('features.colaboradores.faltas.fields.motivoHelper')
          }
          {...form.register('motivo')}
        />
      </Stack>
    </FormDialog>
  );
}
